import Utils from "./utils";
import { LOGS_DESTINATION, LOG_MESSAGES } from "./constants";

/*eslint-disable no-unused-vars*/
class Logger {
  debug(data) {}

  info(data) {}

  warn(data) {}

  error(data) {}

  advancedLog(data) {}
}
/*eslint-enable no-unused-vars*/

const DEFAULT_PREFIX = LOG_MESSAGES.DEFAULT_PREFIX;
const LogLevel = {
  DEBUG: 10,
  INFO: 20,
  WARN: 30,
  ERROR: 40,
  ADVANCED_LOG: 50,
};

class LogManagerImpl {
  constructor(logMetaData) {
    this.logMetaData = logMetaData || '';
    this.updateLoggerConfig();
  }

  hasLogMetaData() {
    return !!this.logMetaData;
  }

  writeToClientLogger(level, logStatement = '') {
    if (!this.hasClientLogger()) {
      return;
    }
    const log1 = typeof logStatement === "string" ? logStatement : JSON.stringify(logStatement, removeCircularReference());
    const log2 = typeof this.logMetaData === "string" ? this.logMetaData : JSON.stringify(this.logMetaData, removeCircularReference());
    const logStringValue = `${getLogLevelByValue(level)} ${log1} ${log2}`;
    switch (level) {
      case LogLevel.DEBUG:
        return this._clientLogger.debug(logStringValue) || logStringValue;
      case LogLevel.INFO:
        return this._clientLogger.info(logStringValue) || logStringValue;
      case LogLevel.WARN:
        return this._clientLogger.warn(logStringValue) || logStringValue;
      case LogLevel.ERROR:
        return this._clientLogger.error(logStringValue) || logStringValue;
      case LogLevel.ADVANCED_LOG:
        if(!this._advancedLogWriter) return '';
        return this._clientLogger[this._advancedLogWriter](logStringValue) || logStringValue;
    }
  }

  isLevelEnabled(level) {
    return level >= this._level;
  }

  hasClientLogger() {
    return this._clientLogger !== null;
  }

  getLogger(options = {}) {
    var prefix = options.prefix || DEFAULT_PREFIX;
    if (options.logMetaData) {
      this.setLogMetaData(options.logMetaData);
    }
    if (!this.logMetaData) {
      console.info("*********Missing required option: WebSocketManager:logMetaData**********");
    }
    return new LoggerWrapperImpl(this, { prefix, logMetaData: this.logMetaData, ...options });
  }

  setLogMetaData(logMetaData) {
    this.logMetaData = logMetaData;
  }

  updateLoggerConfig(inputConfig) {
    var config = inputConfig || {};
    this._level = config.level || LogLevel.INFO;
    //enabled advancedLogWriter
    this._advancedLogWriter = "warn";
    if (config.advancedLogWriter) {
      this._advancedLogWriter = config.advancedLogWriter;
    }

    if(config.customizedLogger && typeof config.customizedLogger === "object") {
      this.useClientLogger = true;
    }
    this._clientLogger = config.logger || this.selectLogger(config);

    this._logsDestination = LOGS_DESTINATION.NULL;
    if (config.debug) {
      this._logsDestination = LOGS_DESTINATION.DEBUG;
    }
    if (config.logger) {
      this._logsDestination = LOGS_DESTINATION.CLIENT_LOGGER;
    }
  }

  selectLogger(config) {
    if(config.customizedLogger && typeof config.customizedLogger === "object") {
      return config.customizedLogger;
    }
    if(config.useDefaultLogger) {
      return createConsoleLogger();
    }
    return null;
  }
}

class LoggerWrapper {
  debug() {}

  info() {}

  warn() {}

  error() {}

  advancedLog() {}
}

class LoggerWrapperImpl extends LoggerWrapper {
  constructor(logManagerInstance, options) {
    super();
    this.options = options || {};
    this.prefix = options.prefix || DEFAULT_PREFIX;
    this.logManager = logManagerInstance;
  }

  debug(...args) {
    return this._log(LogLevel.DEBUG, args);
  }

  info(...args) {
    return this._log(LogLevel.INFO, args);
  }

  warn(...args) {
    return this._log(LogLevel.WARN, args);
  }

  error(...args) {
    return this._log(LogLevel.ERROR, args);
  }

  advancedLog(...args) {
    return this._log(LogLevel.ADVANCED_LOG, args);
  }

  _shouldLog(level) {
    return this.logManager.hasClientLogger() && this.logManager.isLevelEnabled(level);
  }

  _writeToClientLogger(level, logStatement) {
    return this.logManager.writeToClientLogger(level, logStatement);
  }

  _log(level, args) {
    if (this._shouldLog(level)) {
      var logStatement = this.logManager.useClientLogger ? args : this._convertToSingleStatement(args);
      return this._writeToClientLogger(level, logStatement);
    }
  }

  _convertToSingleStatement(args) {
    var date = new Date(Date.now()).toISOString();
    var logStatement = `[${date}]`;
    if (this.prefix) {
      logStatement += this.prefix + " ";
    }
    if (this.options) {
      this.options.prefix ? logStatement += " " + this.options.prefix + ":" : logStatement += "";
    }
    for (var index = 0; index < args.length; index++) {
      var arg = args[index];
      logStatement += this._convertToString(arg) + " ";
    }
    return logStatement;
  }

  _convertToString(arg) {
    try {
      if (!arg) {
        return "";
      }
      if (Utils.isString(arg)) {
        return arg;
      }
      if (Utils.isObject(arg) && Utils.isFunction(arg.toString)) {
        var toStringResult = arg.toString();
        if (toStringResult !== "[object Object]") {
          return toStringResult;
        }
      }
      return JSON.stringify(arg);
    } catch (error) {
      console.error("Error while converting argument to string", arg, error);
      return "";
    }
  }
}

function getLogLevelByValue(value) {
  switch(value) {
    case 10: return "DEBUG";
    case 20: return "INFO";
    case 30: return "WARN";
    case 40: return "ERROR";
    case 50: return "ADVANCED_LOG";
  }
}

function removeCircularReference() {
  const seen = new WeakSet();

  return (key, value) => {
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) {
        return;
      }
      seen.add(value);
    }
    return value;
  };
}

var createConsoleLogger = () => {
  var logger = new LoggerWrapper();
  logger.debug = (...args) => console.debug.apply(window.console, [].concat(args));
  logger.info = (...args) => console.info.apply(window.console, [].concat(args));
  logger.warn = (...args) => console.warn.apply(window.console, [].concat(args));
  logger.error = (...args) => console.error.apply(window.console, [].concat(args));
  return logger;
};

export { LogManagerImpl, Logger, LogLevel };
